import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VersionControlPanel = ({ versions, selectedPolicy }) => {
  if (!selectedPolicy) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 h-full">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="GitBranch" size={20} color="var(--color-primary)" />
          <h3 className="font-heading font-semibold text-foreground">Version Control</h3>
        </div>
        <div className="text-center py-8">
          <Icon name="FileText" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Select a policy to view version history</p>
        </div>
      </div>
    );
  }

  const handleViewVersion = (version) => {
    alert(`Viewing version ${version?.version} from ${version?.date}`);
  };

  const handleCompareVersions = () => {
    alert('Compare versions feature will show side-by-side diff of changes');
  };

  const handleRestoreVersion = (version) => {
    if (confirm(`Restore policy to version ${version?.version}?`)) {
      alert(`Restoring to version ${version?.version}...`);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="GitBranch" size={20} color="var(--color-primary)" />
        <h3 className="font-heading font-semibold text-foreground">Version Control</h3>
      </div>
      {/* Current Version Info */}
      <div className="mb-4 p-3 bg-primary/10 border border-primary rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
          <span className="text-sm font-medium text-primary">Current Version</span>
        </div>
        <p className="text-lg font-bold text-foreground">
          v{versions?.[versions?.length - 1]?.version || '1.0'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Last updated: {versions?.[versions?.length - 1]?.date || 'N/A'}
        </p>
      </div>
      {/* Version History */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Version History</h4>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {versions?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="Clock" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No version history available</p>
            </div>
          ) : (
            versions?.slice()?.reverse()?.map((version, index) => (
              <div
                key={version?.id}
                className={`p-3 rounded-lg border ${
                  version?.isBaseline
                    ? 'border-success bg-success/5' :'border-border bg-card'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-foreground">
                      v{version?.version}
                    </span>
                    {version?.isBaseline && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                        <Icon name="Shield" size={10} />
                        Baseline
                      </span>
                    )}
                    {index === 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                        <Icon name="Star" size={10} />
                        Current
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mb-2">
                  {version?.date} • {version?.author}
                </p>

                <p className="text-xs text-foreground mb-3">{version?.changes}</p>

                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => handleViewVersion(version)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    View
                  </Button>
                  {!version?.isBaseline && index !== 0 && (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => handleRestoreVersion(version)}
                      iconName="RotateCcw"
                      iconPosition="left"
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="space-y-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCompareVersions}
          iconName="GitCompare"
          iconPosition="left"
          fullWidth
          disabled={versions?.length < 2}
        >
          Compare Versions
        </Button>
      </div>
      <div className="mt-4 p-3 bg-muted rounded-md">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={14} color="var(--color-primary)" className="mt-0.5" />
          <div>
            <p className="text-xs font-medium text-foreground mb-1">Version Tracking</p>
            <p className="text-xs text-muted-foreground">
              All changes are tracked with original statutory baseline preserved. You can restore any previous version at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionControlPanel;